/**
 * This type can be consumed both by async wrappers as well as sync hooks,
 * which need to do a check from time to time but cannot await. Note that
 * updating the result value will not trigger re-renders - and that's fine
 * */
export type LookupPromise<T> = Promise<T> & {
  result: T | undefined;
};
export function lookupPromise<T>(promise: Promise<T>) {
  const extended = promise as LookupPromise<T>;
  extended.result = undefined;
  extended.then((result) => {
    extended.result = result;
  });
  return extended;
}

/**
 * This type can either be resolved by the creator or be passed down
 * for someone else to resolve
 */
export type DelegatedPromise<T> = LookupPromise<T> & {
  resolve: (result: T) => void;
};
export function delegatedPromise<F extends () => Promise<unknown>>(fn: F) {
  type T = Awaited<ReturnType<F>>;
  let capturedResolve: DelegatedPromise<T>['resolve'];
  const promise = lookupPromise(
    new Promise((resolve) => {
      capturedResolve = resolve as DelegatedPromise<T>['resolve'];
      fn().then((value) => {
        if (value) resolve(value as T);
      });
    }),
  );
  const extended = promise as DelegatedPromise<T>;
  extended.resolve = (value: T) => void capturedResolve(value);
  return extended;
}
